import { Box, Stack, Typography } from '@mui/material'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useIsMobileWidth } from '@omnea-app/fe-browser-utils/useDetectDevice'
import { ControlledTable } from '@omnea-app/fe-tables/ControlledTable/ControlledTable'
import { useControlledTableState } from '@omnea-app/fe-tables/ControlledTable/hooks/useControlledTableState'
import { useGetSupplierSteps } from '../useGetSupplierSteps'
import { MobileSupplierStepsTable } from './MobileSupplierStepsTable'
import { useGetSuppliersStepsColumns } from './useGetSuppliersStepsColumns'

const SUPPLIER_STEPS_TABLE_NAME = 'OMNEA_SUPPLIER_STEPS_TABLE_NAME'

export const SupplierStepsTable: React.FC = () => {
  const { org } = useAuthenticatedUser()
  const { t } = useTranslation('supplier-dashboard', { keyPrefix: 'tasks-table' })

  const { data: steps, isPending: loading } = useGetSupplierSteps()
  const { push } = useRouter()

  const columns = useGetSuppliersStepsColumns()

  const [values, setters] = useControlledTableState({
    tableName: SUPPLIER_STEPS_TABLE_NAME,
  })

  const isMobileWidth = useIsMobileWidth()

  const toolbarHeader = () => (
    <Typography
      variant="upsized-medium"
      sx={(theme) => ({
        /* FIXME: use a supported typography variant */ fontSize: '1.125rem',
        p: 4,
        [theme.breakpoints.down('md')]: {
          p: 2,
        },
      })}
    >
      {t('table-title', { organisationName: org.name })}
    </Typography>
  )

  if (isMobileWidth) {
    return (
      <Stack gap={3} sx={{ width: '100%', pb: 4 }}>
        <Typography variant="upsized-medium" sx={{ /* FIXME: use a supported typography variant */ fontSize: '1.125rem', p: 2 }}>
          {t('table-title', { organisationName: org.name })}
        </Typography>
        <MobileSupplierStepsTable steps={steps || []} loading={loading} />
      </Stack>
    )
  }

  return (
    <Stack
      gap={5}
      sx={(theme) => ({
        overflow: 'auto',
        width: '100%',
        [theme.breakpoints.down('md')]: {
          gap: theme.spacing(3),
        },
      })}
    >
      {steps && steps.length === 0 && <Typography>{t('no-tasks')}</Typography>}
      {((steps && steps.length !== 0) || !steps) && (
        <Box sx={{ overflowX: 'auto', width: '100%' }}>
          <ControlledTable
            allowPageScroll
            rows={steps || []}
            columns={columns}
            loading={loading}
            tableName={SUPPLIER_STEPS_TABLE_NAME}
            sortModel={values.sortModel}
            onSortModelChange={setters.setSortModel}
            filterModel={values.filterModel}
            onFilterModelChange={setters.setFilterModel}
            paginationModel={values.paginationModel}
            onPaginationModelChange={setters.setPaginationModel}
            columnVisibilityModel={values.columnVisibilityModel}
            onColumnVisibilityModelChange={setters.setColumnVisibilityModel}
            exportOptions={{ fileName: `Omnea_Supplier_Tasks_Export_${new Date().toLocaleDateString('en-GB')}` }}
            onRowClick={({ row }) => {
              const selectedRequestStep = steps?.find((requestStep) => requestStep.id === row.id)
              if (selectedRequestStep) void push(`/supplier-onboarding/${row.requestId}/${selectedRequestStep.id}/overview`)
            }}
            hideRowGrouping
            hideSaveFilterButton
            hideSearchBar
            disableExportInToolbar
            disableColumnSelector
            disableColumnFilter
            toolbarHeader={toolbarHeader()}
          />
        </Box>
      )}
    </Stack>
  )
}
